import { Injectable } from '@angular/core';
import { CoolLocalStorage } from '@angular-cool/storage';
import { BrandingType } from './branding-type.enum';
import { BrandingSettings, Settings } from 'common/settings/settings.interface';
import { BrandingStorageData } from './branding-storage-data.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { BcSettingsService } from 'common/settings/settings.service';
import { LoginSubscriber } from '../subscribers/login-subscriber.service';
import { CommonAuthService } from 'common/auth/common-auth.service';

@Injectable()
export class BrandingService {
  /**
   * The name of the key in the local storage that is used to store the user's preferred branding.
   * @type {string}
   */
  private static readonly BRANDING_STORAGE_KEY = 'bc-branding';

  /**
   * Stores the preferred branding for the currently logged in user in an observable way.
   * @type {BehaviorSubject<BrandingType>}
   * @private
   */
  private _currentBranding: BehaviorSubject<BrandingType>;

  /**
   * Returns the preferred branding for the currently logged in user in an observable way.
   * @returns {BehaviorSubject<BrandingType>}
   */
  public get currentBranding(): Observable<BrandingType> {
    return this._currentBranding.asObservable();
  }

  /**
   * The branding that is used before the currently logged in user's preferred branding is returned by the server,
   * or if the server returns no branding preference.
   * NOTE:
   *       This is used on the login page,
   *       and to make the new frontend work with an older backend, that does not return this information.
   * @type {BrandingType}
   */
  private static defaultBranding: BrandingType = BrandingType.Bold360;

  constructor(private settingsService: BcSettingsService,
              readonly loginSubscriber: LoginSubscriber,
              private localStorage: CoolLocalStorage,
              private commonAuth: CommonAuthService) {

    // Set the default branding based on the value stored after the last login in the browser's local storage.
    const initialBranding = this._getBrandingFromStorageOrDefault();
    this._currentBranding = new BehaviorSubject(initialBranding);
    this._notifySubscribers(initialBranding);

    loginSubscriber.subscribe(this._onLogin.bind(this));
  }

  /**
   * Subscriber handler that is called after the user logged in.
   */
  private async _onLogin() {
    if (!(await this.commonAuth.isBoldEnabled())) {
      return;
    }
    this.settingsService.getSettings().then((settings: Settings) => {
      if (settings) {
        this._setBranding(settings.branding);
      }
    });
  }

  /**
   * Sets the branding of the application based on the current user's branding preference.
   * @param {BrandingSettings} settings - The branding related configuration settings, as they are returned by the BcSettingsService.
   */
  private _setBranding(settings: BrandingSettings): void {
    const branding: BrandingType = !settings
      ? BrandingService.defaultBranding
      : BrandingService._parsePreferredBrandingType(settings.preferred);

    // Save the new branding to the browser's local storage, so the login page can access it the next time.
    this._saveBrandingToStorage(branding);

    // Send the new branding value to the subscribers.
    this._notifySubscribers(branding);
  }

  /**
   * Persists the preferred branding type to the browser's local storage.
   * @param {BrandingType} branding - The branding value to save.
   * @private
   */
  private _saveBrandingToStorage(branding: BrandingType) {
    // Wrap the raw value into an object, so we can extend it later without a deserialization issue.
    const storageData: BrandingStorageData = {
      preferred: branding
    };
    this.localStorage.setObject(BrandingService.BRANDING_STORAGE_KEY, storageData);
  }

  /**
   * Returns the user's preferred branding name from the browser's local storage,
   * or the default branding, if no value, or invalid value is stored in the browser.
   * @returns {BrandingType} - The user's preferred branding.
   * @private
   */
  private _getBrandingFromStorageOrDefault() {
    const storageData: BrandingStorageData = this.localStorage.tryGetObject<BrandingStorageData>(BrandingService.BRANDING_STORAGE_KEY);

    // Validate the raw data read from the browser's local storage.
    return storageData && (storageData.preferred === BrandingType.Bold360 || storageData.preferred === BrandingType.BoldChat)
      ? storageData.preferred
      : BrandingService.defaultBranding;
  }

  /**
   * Sends a notification about a new branding to all subscribed components.
   * @param {BrandingType} newBranding
   * @private
   */
  private _notifySubscribers(newBranding: BrandingType) {
    if (typeof newBranding === 'undefined') {
      throw new Error('Please specify the branding to send to the subscribers!');
    }
    this._currentBranding.next(newBranding);
  }

  /**
   * Parses the branding type returned as a string by the REST API.
   * @param {string} rawBrandingType - The preferred branding of the current user, as it is returned by the REST API.
   * @returns {BrandingType} The parsed enum value of the user's preferred branding.
   */
  private static _parsePreferredBrandingType(rawBrandingType: string): BrandingType {
    switch (rawBrandingType) {
      case 'boldchat':
        return BrandingType.BoldChat;
      case 'bold360':
        return BrandingType.Bold360;
      default:
        return BrandingService.defaultBranding;
    }
  }
}
